import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs';
import * as ibantools from 'ibantools';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../../shared/payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-bank-account-edit',
  templateUrl: './bank-account-edit.component.html',
  styleUrls: ['./bank-account-edit.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    TranslateModule,
  ],
})
export class BankAccountEditComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientsService);
  private fb = inject(FormBuilder);
  private helperService = inject(HelpersService);
  private readonly destroy = new Subject<void>();

  readonly bankAccountForm = this.fb.group({
    accountHolderName: this.fb.control('', { nonNullable: true }),
    accountNumber: this.fb.control('', {
      validators: this.helperService.bankAccountValidator,
      nonNullable: true,
    }),
    bankName: this.fb.control('', { nonNullable: true }),
  });

  readonly formatBankAccount = this.helperService.formatBankAccount;

  saveCount: number = 0;

  get accountHolderName() {
    return this.bankAccountForm.controls.accountHolderName;
  }
  get accountNumber() {
    return this.bankAccountForm.controls.accountNumber;
  }
  get bankName() {
    return this.bankAccountForm.controls.bankName;
  }

  async ngOnInit() {
    this.clientsService.selectedClientSubject
      .pipe(filter((value) => !!value))
      .subscribe({
        next: (client: SelectedClient) => {
          const bankAccount = client.bankAccount;
          if (!bankAccount) {
            return;
          }

          let accountNumber: string;
          if (bankAccount.iban && ibantools.isValidIBAN(bankAccount.iban)) {
            accountNumber = bankAccount.iban;
          } else if (
            bankAccount.account_number &&
            ibantools.isValidBBAN(bankAccount.account_number, 'HU')
          ) {
            accountNumber = bankAccount.account_number;
          } else {
            accountNumber =
              bankAccount.iban || bankAccount.account_number || '';
          }

          this.bankAccountForm.patchValue(
            {
              accountHolderName: bankAccount.account_holder_name ?? '',
              accountNumber: accountNumber,
              bankName: bankAccount.bank_name ?? '',
            },
            { emitEvent: false },
          );
        },
      });

    this.bankAccountForm.valueChanges
      .pipe(takeUntil(this.destroy), debounceTime(777), distinctUntilChanged())
      .subscribe({
        next: async () => await this.saveClient(),
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async saveClient() {
    if (this.bankAccountForm.invalid) {
      return;
    }

    try {
      const { accountHolderName, accountNumber, bankName } =
        this.bankAccountForm.getRawValue();
      const electronicFormatted = ibantools.electronicFormatIBAN(accountNumber);
      if (electronicFormatted == null) return;
      const isIban = ibantools.isValidIBAN(electronicFormatted);
      const isBban = ibantools.isValidBBAN(electronicFormatted, 'HU');

      await this.clientsService.updateBankAccount({
        bank_name: bankName || undefined,
        account_holder_name: accountHolderName || undefined,
        account_number: isBban ? electronicFormatted : undefined,
        iban: isIban ? electronicFormatted : undefined,
      });

      ++this.saveCount;
      setTimeout(() => --this.saveCount, 2500);
    } catch (error) {
      console.error('Error while saving client bank account', error);
    }
  }
}
